import { useState, useEffect, useCallback } from "react";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { StarRating } from "@/components/StarRating";
import { ReviewDialog } from "@/components/ReviewDialog";

export function HostReviews() {
  const { t } = useTranslation();
  const [data, setData] = useState({ summary: { avg: 0, count: 0 }, reviews: [] });
  const [pending, setPending] = useState([]);
  const [reviewTrip, setReviewTrip] = useState(null);

  const load = useCallback(() => {
    api.get("/host/me/reviews").then((r) => setData(r.data)).catch(() => {});
    api.get("/host/me/reviews/pending").then((r) => setPending(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="host-reviews">
      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <p className="font-semibold text-amber-900 mb-3">{t("hostReviews.rateGuests")}</p>
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.booking_id} data-testid={`host-pending-review-${p.booking_id}`} className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                <span className="text-sm text-slate-700"><b>{p.counterparty}</b> · {p.car_name}</span>
                <button onClick={() => setReviewTrip({ booking_id: p.booking_id, car_name: p.car_name, counterparty: p.counterparty })}
                  data-testid={`host-review-btn-${p.booking_id}`} className="rounded-full bg-amber-500 text-white px-4 py-1.5 text-sm font-semibold hover:bg-amber-600">{t("hostReviews.review")}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <span className="font-heading text-3xl font-bold text-slate-900">{data.summary.count ? data.summary.avg.toFixed(1) : "—"}</span>
          <Star size={24} className="fill-amber-400 text-amber-400" />
          <span className="text-slate-500 text-sm">{t("hostReviews.fromGuests", { count: data.summary.count })}</span>
        </div>
        {data.reviews.length === 0 ? (
          <p className="text-slate-400 text-sm" data-testid="host-reviews-empty">{t("hostReviews.empty")}</p>
        ) : (
          <div className="space-y-4">
            {data.reviews.map((r) => (
              <div key={r.id} data-testid={`host-review-${r.id}`} className="border border-slate-100 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Avatar name={r.author_name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{r.author_name}</p>
                    <p className="text-xs text-slate-400">{r.car_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <StarRating value={r.rating} />
                </div>
                {r.comment && <p className="text-slate-600 text-sm mt-2">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {reviewTrip && <ReviewDialog trip={reviewTrip} counterpartyLabel={reviewTrip.counterparty} onClose={() => setReviewTrip(null)} onDone={load} />}
    </div>
  );
}
